import type { ReactNode, SVGProps } from "react";

// Icons of the footer design: a glyph in a rounded square outline. They draw
// in the current text color so that the links can change it on hover.

type FooterSocialIconProps = Omit<SVGProps<SVGSVGElement>, "viewBox">;

const OUTLINE_PATH =
  "M4 0.5H23C24.933 0.5 26.5 2.067 26.5 4V23C26.5 24.933 24.933 26.5 23 26.5H4C2.067 26.5 0.5 24.933 0.5 23V4C0.5 2.067 2.067 0.5 4 0.5Z";

function FooterSocialIcon(
  props: FooterSocialIconProps & { children: ReactNode }
) {
  const { children, ...svgProps } = props;

  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="27"
      viewBox="0 0 27 27"
      width="27"
      xmlns="http://www.w3.org/2000/svg"
      {...svgProps}
    >
      <path d={OUTLINE_PATH} stroke="currentColor" />
      {children}
    </svg>
  );
}

export function FooterFacebookIcon(props: FooterSocialIconProps) {
  return (
    <FooterSocialIcon {...props}>
      <path
        d="M16.767 8.833H15.834C14.835 8.833 14.434 9.066 14.434 9.766V11.166H16.767L16.3 13.499H14.433V20.499H12.1V13.499H10.233V11.166H12.1V9.766C12.1 7.899 13.033 6.499 14.9 6.499C16.253 6.499 16.767 6.966 16.767 6.966V8.833Z"
        fill="currentColor"
      />
    </FooterSocialIcon>
  );
}

export function FooterInstagramIcon(props: FooterSocialIconProps) {
  return (
    <FooterSocialIcon {...props}>
      <path
        clipRule="evenodd"
        d="M16.8575 6.229H10.1425C7.9855 6.229 6.2115 8.003 6.2115 10.16V16.84C6.2115 18.997 7.9855 20.771 10.1425 20.771H16.8575C19.0145 20.771 20.7885 18.997 20.7885 16.84V10.16C20.7885 8.003 19.0145 6.229 16.8575 6.229ZM19.5365 16.805C19.5365 18.301 18.3195 19.519 16.8225 19.519H10.1775C8.6815 19.519 7.4635 18.302 7.4635 16.805V10.195C7.4635 8.699 8.6805 7.482 10.1775 7.482H16.8225C18.3185 7.482 19.5365 8.699 19.5365 10.195V16.805Z"
        fill="currentColor"
        fillRule="evenodd"
      />
      <path
        clipRule="evenodd"
        d="M17.4145 10.438C16.9275 10.438 16.5795 10.056 16.5795 9.603C16.5795 9.15 16.9275 8.768 17.4145 8.768C17.8665 8.768 18.2495 9.15 18.2495 9.603C18.2495 10.056 17.8675 10.438 17.4145 10.438Z"
        fill="currentColor"
        fillRule="evenodd"
      />
      <path
        clipRule="evenodd"
        d="M13.5175 9.534H13.4825C11.3255 9.534 9.5515 11.308 9.5515 13.5C9.5515 15.692 11.3255 17.466 13.4825 17.466H13.5175C15.6745 17.466 17.4835 15.657 17.4835 13.5C17.4835 11.343 15.6745 9.534 13.5175 9.534ZM13.5175 16.144H13.4825C12.0555 16.144 10.8735 14.926 10.8735 13.5C10.8735 12.074 12.0565 10.891 13.4825 10.891H13.5175C14.9445 10.891 16.1265 12.039 16.1265 13.5C16.1265 14.961 14.9435 16.144 13.5175 16.144Z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </FooterSocialIcon>
  );
}

export function FooterLinkedinIcon(props: FooterSocialIconProps) {
  return (
    <FooterSocialIcon {...props}>
      {/* A 24-unit LinkedIn "in" glyph scaled and centred inside the square. */}
      <g transform="translate(5.25 5.25) scale(0.6875)">
        <path
          d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452z"
          fill="currentColor"
        />
      </g>
    </FooterSocialIcon>
  );
}

export function FooterTiktokIcon(props: FooterSocialIconProps) {
  return (
    <FooterSocialIcon {...props}>
      {/* A 24-unit TikTok glyph scaled and centred inside the 27-unit square. */}
      <g transform="translate(5.25 5.25) scale(0.6875)">
        <path
          d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"
          fill="currentColor"
        />
      </g>
    </FooterSocialIcon>
  );
}

export function FooterPinterestIcon(props: FooterSocialIconProps) {
  return (
    <FooterSocialIcon {...props}>
      {/* A 24-unit Pinterest glyph scaled and centred inside the 27-unit square. */}
      <g transform="translate(5.25 5.25) scale(0.6875)">
        <path
          d="M12 2C6.48 2 2 6.48 2 12c0 4.24 2.64 7.86 6.36 9.32-.09-.79-.17-2.01.04-2.88.19-.78 1.21-4.95 1.21-4.95s-.31-.62-.31-1.53c0-1.44.83-2.51 1.87-2.51.88 0 1.31.66 1.31 1.46 0 .89-.56 2.22-.85 3.45-.24 1.03.52 1.87 1.53 1.87 1.84 0 3.25-1.94 3.25-4.73 0-2.47-1.78-4.2-4.31-4.2-2.94 0-4.66 2.2-4.66 4.48 0 .89.34 1.84.77 2.36.08.1.1.19.07.29-.08.33-.26 1.03-.29 1.17-.05.19-.15.23-.35.14-1.3-.6-2.11-2.49-2.11-4.01 0-3.27 2.37-6.27 6.84-6.27 3.59 0 6.38 2.56 6.38 5.98 0 3.57-2.25 6.44-5.37 6.44-1.05 0-2.03-.55-2.37-1.19l-.64 2.46c-.23.89-.86 2.01-1.28 2.69.96.3 1.98.46 3.04.46 5.52 0 10-4.48 10-10S17.52 2 12 2z"
          fill="currentColor"
        />
      </g>
    </FooterSocialIcon>
  );
}

export function FooterXIcon(props: FooterSocialIconProps) {
  return (
    <FooterSocialIcon {...props}>
      <path
        clipRule="evenodd"
        d="M14.798 12.654L18.903 7.962H17.024L13.928 11.501L11.251 7.962H7.374L12.006 14.02L7.616 19.038H9.496L12.885 15.166L15.846 19.038H19.627L14.798 12.654ZM9.568 9.028H10.685L17.406 17.914H16.365L9.568 9.028Z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </FooterSocialIcon>
  );
}

export function FooterYoutubeIcon(props: FooterSocialIconProps) {
  return (
    <FooterSocialIcon {...props}>
      <path
        clipRule="evenodd"
        d="M21.1015 16.589C20.8595 18.406 19.9505 18.679 18.1635 18.831C16.6185 18.982 14.8625 19.013 13.4995 19.013C12.1365 19.013 10.3495 18.983 8.8355 18.831C7.0485 18.68 6.1095 18.407 5.8675 16.589C5.7765 15.801 5.7165 14.651 5.7165 13.5C5.7165 12.349 5.7775 11.229 5.8675 10.411C6.1095 8.624 7.0485 8.321 8.8355 8.169C10.3495 8.048 12.1365 7.987 13.4995 7.987C14.8625 7.987 16.6195 8.048 18.1635 8.169C19.9505 8.32 20.8585 8.624 21.1015 10.411C21.2225 11.229 21.2835 12.349 21.2835 13.5C21.2835 14.651 21.2225 15.802 21.1015 16.589ZM11.7395 11.159L15.7985 13.491L11.7395 15.854V11.159Z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </FooterSocialIcon>
  );
}
